import { computed, reactive, ref, type ComputedRef } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { AdminRole, Permission } from '@jiaofa/constants'
import { fenToYuan, yuanToFen } from '@jiaofa/shared-utils'
import { ApiError } from '@/api/request'
import { createUserMember, deleteUserMember, fetchUserMember, updateUserMember, type AdminMemberProfile, type MemberProfilePayload } from '@/api/member'
import { adjustUserBalance, adjustUserPoints, deleteAdminUser, fetchAdminUser, fetchAdminUserPointsLogs, fetchAdminUsers, fetchAdminUserWalletLogs, updateAdminUserStatus, updateUserAdminPermission, type AdminConsumerUser, type AdminUsersParams, type UserBalancePayload, type UserPointsPayload } from '@/api/user'

type FormatAmount = (amountInFen: number) => string
type FormatSignedAmount = (amountInFen?: number) => string
type FormatSignedNumber = (value?: number) => string

export interface MemberProfileForm {
  levelCode: string
  growthValue: number
  points: number
  balanceYuan: number
  totalPoints: number
  totalRechargeYuan: number
  memberExpireAt: string
  joinedAt: string
}

function memberNotFound(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 404 || error.message.includes('不存在'))
}

function fillMemberProfileForm(form: MemberProfileForm, profile?: AdminMemberProfile): void {
  form.levelCode = profile?.levelCode || 'NORMAL'
  form.growthValue = profile?.growthValue ?? 0
  form.points = profile?.points ?? 0
  form.balanceYuan = Number(fenToYuan(profile?.balance ?? 0))
  form.totalPoints = profile?.totalPoints ?? 0
  form.totalRechargeYuan = Number(fenToYuan(profile?.totalRecharge ?? 0))
  form.memberExpireAt = profile?.memberExpireAt || ''
  form.joinedAt = profile?.joinedAt || ''
}

function toMemberPayload(form: MemberProfileForm): MemberProfilePayload {
  return {
    levelCode: form.levelCode || 'NORMAL',
    growthValue: form.growthValue,
    points: form.points,
    balance: yuanToFen(form.balanceYuan),
    totalPoints: form.totalPoints,
    totalRecharge: yuanToFen(form.totalRechargeYuan),
    memberExpireAt: form.memberExpireAt.trim(),
    joinedAt: form.joinedAt.trim() || null,
  }
}

export function useUserCenter(options: {
  isUserManagementCenter: ComputedRef<boolean>
  isUserBalanceCenter: ComputedRef<boolean>
  isUserPointsCenter: ComputedRef<boolean>
  isUserAssetsCenter: ComputedRef<boolean>
  formatAmount: FormatAmount
  formatSignedAmount: FormatSignedAmount
  formatSignedNumber: FormatSignedNumber
}) {
  const loadingUsers = ref(false)
  const loadingUserWalletLogs = ref(false)
  const loadingUserPointsLogs = ref(false)
  const loadingMemberProfile = ref(false)
  const userPage = ref<Awaited<ReturnType<typeof fetchAdminUsers>>>()
  const userWalletLogPage = ref<Awaited<ReturnType<typeof fetchAdminUserWalletLogs>>>()
  const userPointsLogPage = ref<Awaited<ReturnType<typeof fetchAdminUserPointsLogs>>>()
  const selectedMemberProfile = ref<AdminMemberProfile>()
  const selectedConsumerUser = ref<AdminConsumerUser>()
  const userManagerVisible = ref(false)
  const userBalanceEditorVisible = ref(false)
  const userPointsEditorVisible = ref(false)
  const memberEditorVisible = ref(false)
  const userBalanceSubmitting = ref(false)
  const userPointsSubmitting = ref(false)
  const memberProfileSubmitting = ref(false)
  const userAdminPermissionSubmitting = ref(false)
  const userFilters = reactive<AdminUsersParams>({ page: 1, pageSize: 10, keyword: '', level: '', adminPermission: '' })
  const userWalletLogFilters = reactive({ page: 1, pageSize: 10 })
  const userPointsLogFilters = reactive({ page: 1, pageSize: 10 })
  const userBalanceForm = reactive<UserBalancePayload>({
    amount: 0,
    remark: '',
  })
  const userPointsForm = reactive<UserPointsPayload>({
    points: 0,
    remark: '',
  })
  const memberForm = reactive<MemberProfileForm>({
    levelCode: 'NORMAL',
    growthValue: 0,
    points: 0,
    balanceYuan: 0,
    totalPoints: 0,
    totalRechargeYuan: 0,
    memberExpireAt: '',
    joinedAt: '',
  })

  const userRows = computed(() => userPage.value?.list ?? [])
  const userTotalPages = computed(() => !userPage.value ? 1 : Math.max(1, Math.ceil(userPage.value.total / userPage.value.pageSize)))
  const userManagementStats = computed(() => {
    const list = userRows.value
    return [
      { label: '用户总数', value: String(userPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
      { label: '当前页活跃', value: String(list.filter((item) => ['NORMAL', 'ENABLED', 'ACTIVE'].includes(item.status || '')).length), sub: '正常状态', tone: 'success' as const },
      { label: '黑金会员', value: String(list.filter((item) => item.level === 'BLACK').length), sub: '当前页统计', tone: 'warning' as const },
      { label: '当前页订单', value: String(list.reduce((sum, item) => sum + (item.orders ?? 0), 0)), sub: '累计订单数', tone: 'brand' as const },
    ]
  })
  const userBalanceStats = computed(() => {
    const list = userRows.value
    return [
      { label: '当前页余额', value: options.formatAmount(list.reduce((sum, item) => sum + (item.balance ?? 0), 0)), sub: '可用余额合计', tone: 'brand' as const },
      { label: '有余额用户', value: String(list.filter((item) => (item.balance ?? 0) > 0).length), sub: '当前页统计', tone: 'success' as const },
      { label: '用户总数', value: String(userPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
    ]
  })
  const userPointsStats = computed(() => {
    const list = userRows.value
    return [
      { label: '当前页积分', value: String(list.reduce((sum, item) => sum + (item.points ?? 0), 0)), sub: '积分总量', tone: 'brand' as const },
      { label: '当前页成长值', value: String(list.reduce((sum, item) => sum + (item.growth ?? 0), 0)), sub: '成长值总量', tone: 'success' as const },
      { label: '高等级用户', value: String(list.filter((item) => ['GOLD', 'BLACK'].includes(item.level || '')).length), sub: '黄金及以上', tone: 'warning' as const },
      { label: '用户总数', value: String(userPage.value?.total ?? 0), sub: '后端分页总量', tone: 'brand' as const },
    ]
  })
  const userAssetsStats = computed(() => {
    const walletCount = userWalletLogPage.value?.total ?? 0
    const pointsCount = userPointsLogPage.value?.total ?? 0
    return [
      { label: '余额流水', value: String(walletCount), sub: '后端分页总量', tone: 'brand' as const },
      { label: '积分流水', value: String(pointsCount), sub: '后端分页总量', tone: 'success' as const },
      { label: '当前页余额变动', value: options.formatSignedAmount((userWalletLogPage.value?.list ?? []).reduce((sum, item) => sum + (item.changeAmount ?? 0), 0)), sub: '本页合计', tone: 'warning' as const },
      { label: '当前页积分变动', value: options.formatSignedNumber((userPointsLogPage.value?.list ?? []).reduce((sum, item) => sum + (item.changePoints ?? 0), 0)), sub: '本页合计', tone: 'brand' as const },
    ]
  })

  async function loadUsers(): Promise<void> {
    if (!options.isUserManagementCenter.value && !options.isUserBalanceCenter.value && !options.isUserPointsCenter.value) return
    loadingUsers.value = true
    try {
      userPage.value = await fetchAdminUsers({
        page: userFilters.page,
        pageSize: userFilters.pageSize,
        keyword: userFilters.keyword || undefined,
        level: userFilters.level || undefined,
        adminPermission: userFilters.adminPermission || undefined,
      })
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '用户列表加载失败')
    } finally {
      loadingUsers.value = false
    }
  }

  async function loadUserAssets(): Promise<void> {
    loadingUserWalletLogs.value = true
    loadingUserPointsLogs.value = true
    try {
      let seedUser = selectedConsumerUser.value?.id ?? userRows.value[0]?.id
      if (!seedUser) {
        const page = await fetchAdminUsers({
          page: 1,
          pageSize: 1,
          keyword: userFilters.keyword || undefined,
          level: userFilters.level || undefined,
          adminPermission: userFilters.adminPermission || undefined,
        })
        selectedConsumerUser.value = page.list[0]
        seedUser = page.list[0]?.id
      }
      if (!seedUser) {
        userWalletLogPage.value = undefined
        userPointsLogPage.value = undefined
        return
      }
      selectedConsumerUser.value = selectedConsumerUser.value?.id === seedUser
        ? selectedConsumerUser.value
        : await fetchAdminUser(seedUser)
      const [walletPage, pointsPage] = await Promise.all([
        fetchAdminUserWalletLogs(seedUser, { page: userWalletLogFilters.page, pageSize: userWalletLogFilters.pageSize }),
        fetchAdminUserPointsLogs(seedUser, { page: userPointsLogFilters.page, pageSize: userPointsLogFilters.pageSize }),
      ])
      userWalletLogPage.value = walletPage
      userPointsLogPage.value = pointsPage
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '用户资产流水加载失败')
    } finally {
      loadingUserWalletLogs.value = false
      loadingUserPointsLogs.value = false
    }
  }

  async function searchUsers(): Promise<void> {
    userFilters.page = 1
    if (options.isUserAssetsCenter.value) {
      const page = await fetchAdminUsers({
        page: 1,
        pageSize: 1,
        keyword: userFilters.keyword || undefined,
        level: userFilters.level || undefined,
        adminPermission: userFilters.adminPermission || undefined,
      })
      selectedConsumerUser.value = page.list[0]
      userWalletLogFilters.page = 1
      userPointsLogFilters.page = 1
      await loadUserAssets()
      return
    }
    await loadUsers()
  }

  async function resetUserFilters(): Promise<void> {
    userFilters.keyword = ''
    userFilters.level = ''
    userFilters.adminPermission = ''
    userFilters.page = 1
    if (options.isUserAssetsCenter.value) {
      selectedConsumerUser.value = undefined
      userWalletLogFilters.page = 1
      userPointsLogFilters.page = 1
      await loadUserAssets()
      return
    }
    await loadUsers()
  }

  async function changeUserPage(page: number): Promise<void> {
    userFilters.page = page
    await loadUsers()
  }

  async function changeUserAssetPage(page: number): Promise<void> {
    userWalletLogFilters.page = page
    userPointsLogFilters.page = page
    await loadUserAssets()
  }

  async function refreshSelectedUser(userId: string): Promise<void> {
    const latestUser = await fetchAdminUser(userId)
    selectedConsumerUser.value = latestUser
    if (userPage.value) {
      userPage.value = {
        ...userPage.value,
        list: userPage.value.list.map((item) => item.id === userId ? { ...item, ...latestUser } : item),
      }
    }
  }

  function hasUserAdminOrderPermission(user?: AdminConsumerUser): boolean {
    const roles = user?.roles ?? []
    const permissions = user?.permissions ?? []
    return Boolean(
      user?.hasOrderManagePermission ||
      permissions.includes(Permission.All) ||
      permissions.includes(Permission.OrderView) ||
      permissions.includes(Permission.OrderEdit) ||
      roles.includes(AdminRole.SuperAdmin) ||
      roles.includes(AdminRole.StoreManager) ||
      roles.includes(AdminRole.StoreStaff),
    )
  }

  async function loadMemberProfile(userId: string): Promise<void> {
    loadingMemberProfile.value = true
    try {
      selectedMemberProfile.value = await fetchUserMember(userId)
      fillMemberProfileForm(memberForm, selectedMemberProfile.value)
    } catch (error) {
      if (memberNotFound(error)) {
        selectedMemberProfile.value = undefined
        fillMemberProfileForm(memberForm)
      } else {
        ElMessage.error(error instanceof Error ? error.message : '会员档案加载失败')
      }
    } finally {
      loadingMemberProfile.value = false
    }
  }

  function openUserBalanceEditor(item?: AdminConsumerUser): void {
    selectedConsumerUser.value = item ?? userRows.value[0]
    if (!selectedConsumerUser.value) {
      ElMessage.warning('当前没有可调整的用户')
      return
    }
    userBalanceForm.amount = 0
    userBalanceForm.remark = ''
    userBalanceEditorVisible.value = true
  }

  function openUserPointsEditor(item?: AdminConsumerUser): void {
    selectedConsumerUser.value = item ?? userRows.value[0]
    if (!selectedConsumerUser.value) {
      ElMessage.warning('当前没有可调整的用户')
      return
    }
    userPointsForm.points = 0
    userPointsForm.remark = ''
    userPointsEditorVisible.value = true
  }

  async function openUserManager(item: AdminConsumerUser): Promise<void> {
    selectedConsumerUser.value = item
    userManagerVisible.value = true
    userWalletLogFilters.page = 1
    userPointsLogFilters.page = 1
    await Promise.all([loadUserAssets(), loadMemberProfile(item.id)])
  }

  function openMemberEditor(): void {
    if (!selectedConsumerUser.value) {
      ElMessage.warning('请先选择用户')
      return
    }
    fillMemberProfileForm(memberForm, selectedMemberProfile.value)
    memberEditorVisible.value = true
  }

  async function submitMemberProfile(): Promise<void> {
    if (!selectedConsumerUser.value) {
      ElMessage.warning('请先选择用户')
      return
    }
    if (!memberForm.levelCode.trim()) {
      ElMessage.warning('请选择会员等级')
      return
    }
    memberProfileSubmitting.value = true
    try {
      const userId = selectedConsumerUser.value.id
      const payload = toMemberPayload(memberForm)
      const isCreating = !selectedMemberProfile.value
      const savedProfile = selectedMemberProfile.value
        ? await updateUserMember(userId, payload)
        : await createUserMember(userId, payload)
      selectedMemberProfile.value = savedProfile
      fillMemberProfileForm(memberForm, savedProfile)
      memberEditorVisible.value = false
      await Promise.all([
        loadMemberProfile(userId),
        refreshSelectedUser(userId),
        loadUsers(),
      ])
      ElMessage.success(isCreating ? '会员档案已创建' : '会员档案已保存')
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '保存会员档案失败')
    } finally {
      memberProfileSubmitting.value = false
    }
  }

  async function removeMemberProfile(): Promise<void> {
    if (!selectedConsumerUser.value || !selectedMemberProfile.value) return
    try {
      await ElMessageBox.confirm('确定删除该用户的会员档案吗？删除为软删除，不影响历史订单和资产流水。', '删除会员档案', {
        type: 'warning',
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
      })
    } catch {
      return
    }
    try {
      await deleteUserMember(selectedConsumerUser.value.id)
      selectedMemberProfile.value = undefined
      fillMemberProfileForm(memberForm)
      ElMessage.success('会员档案已删除')
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '删除会员档案失败')
    }
  }

  async function toggleUserStatus(item: AdminConsumerUser): Promise<void> {
    const isFrozen = item.status === 'FROZEN' || item.status === 'DISABLED'
    const nextStatus = isFrozen ? 'ACTIVE' : 'FROZEN'
    const actionText = isFrozen ? '解封' : '封禁'
    try {
      await ElMessageBox.confirm(`确定${actionText}用户“${item.nickname || item.phone || item.id}”吗？`, `${actionText}用户`, {
        type: 'warning',
        confirmButtonText: `确认${actionText}`,
        cancelButtonText: '取消',
      })
    } catch {
      return
    }
    try {
      await updateAdminUserStatus(item.id, { status: nextStatus })
      ElMessage.success(`用户已${actionText}`)
      await loadUsers()
      if (selectedConsumerUser.value?.id === item.id) {
        selectedConsumerUser.value = await fetchAdminUser(item.id)
      }
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : `${actionText}用户失败`)
    }
  }

  async function submitUserAdminPermission(enabled: boolean): Promise<void> {
    if (!selectedConsumerUser.value) {
      ElMessage.warning('请先选择用户')
      return
    }
    userAdminPermissionSubmitting.value = true
    try {
      const userId = selectedConsumerUser.value.id
      const latestUser = await updateUserAdminPermission(userId, {
        enabled,
        roles: enabled ? [AdminRole.StoreStaff] : [],
        permissions: enabled ? [Permission.OrderView, Permission.OrderEdit] : [],
      })
      selectedConsumerUser.value = latestUser
      if (userPage.value) {
        userPage.value = {
          ...userPage.value,
          list: userPage.value.list.map((item) => item.id === userId ? { ...item, ...latestUser } : item),
        }
      }
      ElMessage.success(enabled ? '已开启订单管理权限' : '已关闭订单管理权限')
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '管理员权限保存失败')
    } finally {
      userAdminPermissionSubmitting.value = false
    }
  }

  async function removeUser(item: AdminConsumerUser): Promise<void> {
    try {
      await ElMessageBox.confirm(`确定删除用户“${item.nickname || item.phone || item.id}”吗？删除后列表将不再展示。`, '删除用户', {
        type: 'warning',
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
      })
    } catch {
      return
    }
    try {
      await deleteAdminUser(item.id)
      ElMessage.success('用户已删除')
      if (selectedConsumerUser.value?.id === item.id) {
        selectedConsumerUser.value = undefined
        userManagerVisible.value = false
      }
      await loadUsers()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '删除用户失败')
    }
  }

  async function submitUserBalance(): Promise<void> {
    if (!selectedConsumerUser.value) {
      ElMessage.warning('请先选择用户')
      return
    }
    if (!userBalanceForm.amount) {
      ElMessage.warning('请输入调整金额')
      return
    }
    userBalanceSubmitting.value = true
    try {
      const userId = selectedConsumerUser.value.id
      await adjustUserBalance(userId, {
        amount: yuanToFen(userBalanceForm.amount),
        remark: userBalanceForm.remark?.trim() || undefined,
      })
      ElMessage.success('用户余额已调整')
      userBalanceEditorVisible.value = false
      await loadUsers()
      await refreshSelectedUser(userId)
      if (userManagerVisible.value || options.isUserAssetsCenter.value) await loadUserAssets()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '余额调整失败')
    } finally {
      userBalanceSubmitting.value = false
    }
  }

  async function submitUserPoints(): Promise<void> {
    if (!selectedConsumerUser.value) {
      ElMessage.warning('请先选择用户')
      return
    }
    if (!userPointsForm.points) {
      ElMessage.warning('请输入调整积分')
      return
    }
    userPointsSubmitting.value = true
    try {
      const userId = selectedConsumerUser.value.id
      await adjustUserPoints(userId, {
        points: userPointsForm.points,
        remark: userPointsForm.remark?.trim() || undefined,
      })
      ElMessage.success('用户积分已调整')
      userPointsEditorVisible.value = false
      await loadUsers()
      await refreshSelectedUser(userId)
      if (userManagerVisible.value || options.isUserAssetsCenter.value) await loadUserAssets()
    } catch (error) {
      ElMessage.error(error instanceof Error ? error.message : '积分调整失败')
    } finally {
      userPointsSubmitting.value = false
    }
  }

  return {
    loadingUsers,
    loadingUserWalletLogs,
    loadingUserPointsLogs,
    loadingMemberProfile,
    userPage,
    userWalletLogPage,
    userPointsLogPage,
    selectedMemberProfile,
    selectedConsumerUser,
    userManagerVisible,
    userBalanceEditorVisible,
    userPointsEditorVisible,
    memberEditorVisible,
    userBalanceSubmitting,
    userPointsSubmitting,
    memberProfileSubmitting,
    userAdminPermissionSubmitting,
    userFilters,
    userBalanceForm,
    userPointsForm,
    memberForm,
    userRows,
    userTotalPages,
    userManagementStats,
    userBalanceStats,
    userPointsStats,
    userAssetsStats,
    loadUsers,
    loadUserAssets,
    searchUsers,
    resetUserFilters,
    changeUserPage,
    changeUserAssetPage,
    openUserManager,
    openMemberEditor,
    openUserBalanceEditor,
    openUserPointsEditor,
    hasUserAdminOrderPermission,
    toggleUserStatus,
    removeUser,
    submitUserBalance,
    submitUserPoints,
    submitMemberProfile,
    submitUserAdminPermission,
    removeMemberProfile,
  }
}
